<template>
  <div class="base base-form">
    <div-container full="true" class="main-div" column-num="is-12"
      v-loading.fullscreen="loading" style="margin:0;padding:0;margin-left:0;margin-right:0;">

      <el-form
        v-if="visible"
        ref="form"
        class="this-form" autoComplete="on"
        :model="form"
        :rules="rules"
        label-position="left">

        <!-- layout base: columns -->
        <div class="columns is-multiline" :class="columnsClass">
          <div
            v-for="(column, index) in columnsVisible"
            :key="`${index}`"
            :style="[column.styleColumn, (column.isSpan ? { paddingBottom: 0 } : {})]"
            class="column" :class="column.class || 'is-3'">

            <span v-if="column.isSpan"></span>

            <!-- label -->
            <form-label
              v-else-if="column.type === 'label'"
              :column="column" />

            <!-- input -->
            <form-input
              v-else-if="column.type === 'input'"
              :column="column"
              :form="form"
              :data="data"
              :disabled="getStateDisabled(column)"
              @on-input="onInput"
              @on-select-changed="onInputSelectChanged"
              @on-button="onButtonClicked" />

            <!-- input-list -->
            <form-input-list
              v-else-if="column.type === 'input-list'"
              :column="column"
              :form="form"
              :data="data"
              :rules="rules"
              :rule-require-at-least-once="column.requireAtLeastOnce"
              :disabled="getStateDisabled(column)"
              @on-input="onInput"
              @on-delete="onControlDelete"
              @on-add="onControlAdd" />

            <!-- select -->
            <form-select
              v-else-if="column.type === 'select'"
              :findex="findex"
              :column="column"
              :form="form"
              :options="getData(column.data)"
              :method="getMethod(column.remoteMethod)"
              :disabled="getStateDisabled(column)"
              @on-select-changed="onSelectChanged" />

            <!-- select-list -->
            <form-select-list
              v-else-if="column.type === 'select-list'"
              :findex="findex"
              :column="column"
              :form="form"
              :options="getData(column.data)"
              :rules="rules"
              :rule-require-at-least-once="column.requireAtLeastOnce"
              :disabled="getStateDisabled(column)"
              @on-select-changed="onSelectChanged"
              @on-button="onButtonClicked" />

            <!-- upload -->
            <el-form-item
              v-else-if="column.type === 'upload'"
              :label="column.label"
              :prop="column.name"
              class="upload-not-padded pos-relative"
              :class="((column.labelCustom != null || column.prefixInfo != null) ? 'with-custom-label' : '')"
              :style="column.style">

              <template v-if="column.labelCustom != null || column.prefixInfo != null" slot="label">
                <span class="inline-middle">{{ column.label }}</span>

                <el-tooltip v-if="column.prefixInfo" effect="dark"
                  placement="top"
                  popper-class="tooltip-content">
                  <div slot="content" v-html="column.prefixInfo.label"></div>
                  <i class="header-icon el-icon-info tooltip-info-icon"></i>
                </el-tooltip>

                <a v-if="column.labelCustom && column.labelCustom.type === 'link'"
                  class="inline-middle color-primary"
                  href="#" @click="$event.preventDefault();onLink(column.labelCustom.url, column.labelCustom.isExternal)"
                  style="margin-left:12px;font-size:0.8em;">
                  {{ column.labelCustom.label }}
                  <i class="fa fa-external-link" style="margin-left:2px;"></i>
                </a>

              </template>

              <el-button v-if="column.isCustomUpload"
                type="primary uppercase" style="width:100%;"
                @click="onButtonClicked(column)"
                :disabled="getStateDisabled(column)">
                {{ column.isCustomUploadLabel || 'Upload/Gallery' }}
              </el-button>

              <upload
                :index="findex"
                :field="column.name"
                :accept="column.accept || 'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf'"
                :list-type="column.listType || 'text'"
                :limit="column.limit || 1"
                :file-list="form[column.name]"
                :disabled="getStateDisabled(column)"
                :button-label="column.buttonLabel"
                :button-style="column.buttonStyle"
                :tip="column.tip"
                :is-drop="column.isAutoUpload"
                :is-auto-upload="column.isAutoUpload"
                :is-upload-hidden="column.isUploadHidden"
                @on-file-preview="onFilePreview"
                @on-file-exceed="onFileExceed"
                @on-file-change="onFileChange"
                @on-file-remove="onFileChange"
                @on-file-success="onFileSuccess">
                <!-- <el-button type="primary">Upload/Gallery</el-button> -->
              </upload>

              <!-- tooltip if not custom label -->
              <!-- <div v-if="column.prefixInfo && !(column.labelCustom && column.labelCustom.type === 'link')"
                class="pos-absolute"
                :style="column.prefixInfo.hasOwnProperty('styleWrapper') ?
                  column.prefixInfo.styleWrapper :
                  { top: '0', left: '100px' }">
                <el-tooltip effect="dark"
                  placement="top"
                  popper-class="tooltip-content">
                  <div slot="content" v-html="column.prefixInfo.label"></div>
                  <i class="header-icon el-icon-info tooltip-info-icon"></i>
                </el-tooltip>
              </div> -->
            </el-form-item>

            <!-- ratio-group -->
            <el-form-item
              v-else-if="column.type === 'radio-group'"
              :label="column.label"
              :prop="column.name"
              :style="column.style">
              <el-radio-group
                v-model="form[column.name]"
                :size="column.radioGroupSize || 'small'"
                :disabled="getStateDisabled(column)"
                @change="onRadioGroupChanged(column, { value: form[column.name] })">
                <el-radio-button
                  v-for="(item, rindex) in getData(column.data)"
                  :key="`${rindex}`"
                  :label="item.id">
                  {{ item.label }}
                </el-radio-button>
              </el-radio-group>
            </el-form-item>

            <!-- date cascader -->
            <form-date-picker
              v-else-if="column.type === 'date_picker'"
              :column="column"
              :form="form"
              :options="getData(column.data)"
              :disabled="getStateDisabled(column)"
              @on-range-changed="onDatePickerRangeChanged" />

            <!-- date cascader -->
            <form-date-cascader
              v-else-if="column.type === 'date_cascader'"
              :column="column"
              :form="form"
              :options="getData(column.data)"
              :disabled="getStateDisabled(column)"
              @on-range-changed="onDateCascaderRangeChanged" />

            <!-- headers -->
            <div v-else-if="column.type === 'header'" :style="column.style" style="margin-top:10px;margin-bottom:10px;">
              <h1 class="inline-middle" v-if="column.headerType === 'h1'">{{ column.label }}</h1>
              <h2 class="inline-middle" v-else-if="column.headerType === 'h2'">{{ column.label }}</h2>
              <h3 class="inline-middle" v-else-if="column.headerType === 'h3'">{{ column.label }}</h3>
              <h4 class="inline-middle" v-else-if="column.headerType === 'h4'">{{ column.label }}</h4>
              <h5 class="inline-middle" v-else-if="column.headerType === 'h5'">{{ column.label }}</h5>
              <h6 class="inline-middle" v-else-if="column.headerType === 'h6'">{{ column.label }}</h6>
              <h4 class="inline-middle" v-else>{{ column.label }}</h4>

              <div class="inline-middle" v-if="column.attach">
                <el-form-item
                   v-if="column.attach.type === 'checkbox'"
                  :prop="column.attach.name"
                  :label="undefined"
                  :style="column.attach.style"
                  style="margin-bottom:0;margin-left:14px;">
                  <el-checkbox
                    v-model="form[column.attach.name]"
                    style="width:100%;"
                    :disabled="column.attach.disabled || false"
                    @change="onCheckboxChanged(column.attach.name, column.attach, form[column.attach.name])"
                    :style="column.attach.styleControl">
                    {{ column.attach.label }}
                  </el-checkbox>
                </el-form-item>
              </div>

              <el-tooltip v-if="column.prefixInfo" effect="dark"
                placement="right"
                style="margin-left:8px;"
                popper-class="tooltip-content">
                <span slot="content" v-html="column.prefixInfo.label"></span>
                <i class="header-icon el-icon-info"></i>
              </el-tooltip>

            </div>

            <!-- hr -->
            <hr v-else-if="column.type === 'hr'"
              style="max-width:100%;border-top:1px solid #e0e0e0;" :style="column.controlStyle" />

            <!-- span -->
            <span v-else-if="column.type === 'span' || column.type === 'stub'"></span>

            <!-- button -->
            <el-form-item
              v-else-if="column.type === 'button'"
              :label="column.label"
              :prop="column.name"
              :style="column.style"
              :class="column.classFormItem">
              <el-button
                :type="column.buttonType"
                :style="column.buttonStyle"
                :disabled="getStateDisabled(column)"
                :size="column.buttonSize"
                :icon="column.buttonIcon"
                @click="onButtonClicked(column)">
                {{ column.buttonLabel || column.label || 'Submit' }}
                <span
                  v-if="column.buttonGenerate"
                  class="button-generator"
                  :class="(!signal ? 'is-invalid' : signal.isGeneratedChanged ? 'is-red' : signal.isGenerated ? 'is-green' : '')"
                  style="margin-left:8px;">
                </span>
                <span v-if="column.isExternal">&nbsp;&nbsp;<i class="fa fa-external-link"></i></span>
              </el-button>
            </el-form-item>

            <!-- radio -->
            <el-form-item
              v-else-if="column.type === 'radio' && column.radioType === 'button'"
              :label="column.label"
              :prop="column.name"
              :style="column.style">
              <el-radio-group
                v-model="form[column.name]"
                :disabled="getStateDisabled(column)"
                @change="onRadioButtonChanged(column.name, column, form[column.name])"
                :style="column.styleGroup"
                :size="column.size || 'medium'">
                <el-radio-button
                  v-for="(radio, index) in column.data"
                  :key="`${index}`"
                  :name="radio.name"
                  :label="radio.id">
                  {{ radio.label }}
                </el-radio-button>
              </el-radio-group>
            </el-form-item>

            <!-- radio -->
            <el-form-item
              v-else-if="column.type === 'radio'"
              :label="column.label"
              :prop="column.name"
              :style="column.style">
              <el-radio-group
                v-model="form[column.name]"
                :disabled="getStateDisabled(column)"
                @change="onRadioButtonChanged(column.name, column, form[column.name])"
                :style="column.styleGroup"
                :size="column.size || 'medium'">
                <el-radio
                  v-for="(radio, index) in column.data"
                  :key="`${index}`"
                  :name="radio.name"
                  :label="radio.id">
                  {{ radio.label }}
                </el-radio>
              </el-radio-group>
            </el-form-item>

            <!-- checkbox -->
            <!-- special character (space) label -->
            <el-form-item
               v-else-if="column.type === 'checkbox'"
              :prop="column.name"
              :label="column.noGap ? undefined : '‌‌ '"
              :style="column.style">
              <el-checkbox
                v-model="form[column.name]"
                style="width:100%;"
                :disabled="getStateDisabled(column)"
                @change="onCheckboxChanged(column.name, column, form[column.name])"
                :style="column.styleControl">
                {{ column.label }}
              </el-checkbox>
            </el-form-item>

            <!-- slot -->
            <div v-else-if="column.type === 'slot'">
              <slot :name="column.slotName">
              </slot>
            </div>

            <span v-else>
              [invalid field]
            </span>

          </div>
        </div>

      </el-form>

    </div-container>
  </div>
</template>

<script>
import _ from 'lodash'
import Util from '@/util'
import Constants from '@/constants'

import DivContainer from '@/components/DivContainer.vue'
import FormLabel from './formlabel'
import FormInput from './forminput'
import FormInputList from './forminputlist'
import FormSelect from './formselect'
import FormSelectList from './formselectlist'
import FormDatePicker from './formdatepicker'
import FormDateCascader from './formdatecascader'
import Upload from './upload'

export default {
  name: 'base-form',
  components: {
    DivContainer,
    FormLabel,
    FormInput,
    FormInputList,
    FormSelect,
    FormSelectList,
    FormDatePicker,
    FormDateCascader,
    Upload
  },
  props: ['refresh-code', 'columns', 'form', 'rules', 'data', 'signal', 'findex', 'fname', 'disabled', 'columns-class'],
  data () {
    return {
      loading: false,
      visible: true
    }
  },
  computed: {
    modules: function () {
      return this.$store.getters.user.modules
    },
    Util: function () {
      return Util
    },
    Constants: function () {
      return Constants
    },
    columnsVisible: function () {
      let columns = this.columns
      if (columns && Array.isArray(columns)) {
        return _.filter(columns, (item) => !item.hidden)
      }
      return []
    }
  },
  watch: {
    refreshCode: async function () {
      await this.refresh()
    },

    // data: function (value) {
    //   console.log('-- data watcher', value)
    // },

    // datas: function (value) {
    //   console.log('-- datas watcher', value)
    // }
  },
  created () {
  },
  mounted () {
    this.refresh()
  },
  methods: {
    async refresh () {
    },

    getStateDisabled (column) {
      // form state
      let form = this.form
      if (form.hasOwnProperty(`${column.name}State`)) {
        let state = form[`${column.name}State`]
        if (state.hasOwnProperty('disabled')) {
          return state.disabled
        }
      }
      // column
      if (column.hasOwnProperty('disabled') && typeof column.disabled === 'function') {
        return column.disabled()
      } if (column.hasOwnProperty('disabled')) {
        return column.disabled
      }
      // ignore disabled
      if (column.hasOwnProperty('ignoreDisabled') && column.ignoreDisabled) {
        return false
      }
      return this.disabled
    },

    getData (name) {
      // console.log('-- get data: ', {
      //   name,
      //   data: this.data
      // })
      if (this.data && this.data[name] && typeof this.data[name] === 'object') {
        return this.data[name]
      }
      return []
    },

    getMethod (name) {
      // console.log('-- get method: ', {
      //   name,
      //   data: this.data
      // })
      if (typeof name === 'function') return name
      if (this.data && this.data[name] && typeof this.data[name] === 'function') {
        return this.data[name]
      }
      // console.log('-- no method', {
      //   data: this.data,
      //   name
      // })
    },

    validate () {
      if (this.$refs.form) {
        return this.$refs.form.validate()
      }
    },

    validateField (field) {
      if (this.$refs.form) {
        return this.$refs.form.validateField(field)
      }
    },

    resetFields () {
      if (this.$refs.form) {
        return this.$refs.form.resetFields()
      }
    },

    clearValidate () {
      if (this.$refs.form) {
        this.$refs.form.clearValidate()
      }
    },

    formInfo () {
      return { fname: this.fname, findex: this.findex }
    },

    onLink (url, isExternal) {
      Util.url.openUrl(url, isExternal)
    },

    onInput (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-input', info)
    },

    onInputSelectChanged (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-input-select-changed', info)
    },

    onSelectChanged (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-select-changed', info)
    },

    onRadioButtonChanged (name, column, value) {
      let info = _.assign({}, column, { value, ...this.formInfo() })
      this.$emit('on-radio-button-changed', info)
    },

    onCheckboxChanged (name, column, value) {
      let info = _.assign({}, column, { value, ...this.formInfo() })
      this.$emit('on-checkbox-changed', info)
    },

    onButtonClicked (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-button-clicked', info)
    },

    onRadioGroupChanged (info, data) {
      info = _.assign({}, info, this.formInfo())
      info = _.assign(info, data)
      this.$emit('on-radio-group-changed', info)
    },

    onDatePickerRangeChanged (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-date-picker-range-changed', info)
    },

    onDateCascaderRangeChanged (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-date-cascader-range-changed', info)
    },

    // upload modules
    onFilePreview (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-file-preview', info)
    },

    onFileExceed (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-file-exceed', info)
    },

    onFileChange (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-file-change', info)
    },

    onFileSuccess (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-file-success', info)
    },

    // list controls
    onControlDelete (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-control-delete', info)
    },

    onControlAdd (info) {
      info = _.assign({}, info, this.formInfo())
      this.$emit('on-control-add', info)
    }
  }
}
</script>

<style lang="scss">
@import "../styles/base.scss";
// @import "../styles/input_color.scss";

.base-form {
  // bulma & element-ui item group margin are contradicting
  .el-form {
    .columns {
      .column {
        padding-top: 0;
        padding-bottom: 4px;
      }
    }
  }

  .section {
    &.inner {
      margin-left: -15px;
      margin-right: -15px;
      padding-left: 15px;
      padding-right: 15px;
    }
  }

  .el-button {
    text-transform: uppercase;
    .button-generator {
      position: absolute;
      display: inline-block;
      left: 10px;
      height: 10px;
      width: 10px;
      background-color: lightgray;
      border-radius: 50%;
      &.is-red {
        background-color: red;
      }
      &.is-green {
        background-color: #5CD86D;
      }
      &.is-invalid {
        background-color: black;
      }
    }
  }

  .with-custom-label {
    display: flex;
    flex-direction: column;
    // .el-form-item__label {
    //   z-index: 1;
    // }
    // .el-form-item__content {
    //   z-index: -1;
    // }
  }
}

</style>
